import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  format,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  applyRangeDatePick,
  areIntervalBoundsSameMonth,
  type DateCriteriaRange,
  isDayInRange,
  isDayRangeEndpoint,
  formatIntervalDisplayValue,
  isIntervalRangeComplete,
  isValidYyyyMmDd,
  normalizeIntervalRangeOrder,
} from "./u2000DateCriteriaUtils";
import U2000SearchCriteriaIntervalPresetsPanel from "./U2000SearchCriteriaIntervalPresetsPanel";

/** Nombre de semaines affichées — identique sur tous les panneaux pour aligner début/fin. */
const CALENDAR_GRID_WEEKS = 6;
const CALENDAR_GRID_DAY_COUNT = CALENDAR_GRID_WEEKS * 7;

/** 2 × 18rem + gap-3 ; hauteur alignée entre 1 et 2 calendriers. */
const DUAL_CALENDAR_SLOT_CLASS =
  "grid w-full min-w-0 grid-cols-1 gap-3 md:min-h-110 md:min-w-147 md:grid-cols-2 md:items-stretch";
const DUAL_CALENDAR_PANEL_CLASS = "flex h-full min-h-110 min-w-0 w-full flex-col";

export interface U2000SearchCriteriaCalendarProps {
  calendarCount?: 1 | 2;
  range?: DateCriteriaRange;
  onRangeChange: (range: DateCriteriaRange) => void;
  disabled?: boolean;
  /** `row` : panneaux côte à côte (dialogue) ; `stack` : empilés */
  layout?: "row" | "stack";
}

interface BoundEditorConfig {
  panelIndex: number;
  boundLabel: string;
  inputValue: string;
  onInputFocus: () => void;
  onInputChange: (value: string) => void;
  onInputCommit: () => void;
  onTodayClick: () => void;
  onClearClick: () => void;
}

interface MonthPanelProps {
  month: Date;
  onMonthChange: (month: Date) => void;
  range: DateCriteriaRange;
  onDayClick: (ymd: string) => void;
  boundEditors: BoundEditorConfig[];
  today: Date;
  monthNames: string[];
  dayNames: string[];
  todayLabel: string;
  clearBoundLabel: string;
  prevMonthLabel: string;
  nextMonthLabel: string;
  calendarGridLabel: string;
  disabled: boolean;
  /** Occupe la largeur des deux calendriers côte à côte */
  expanded?: boolean;
  /** Panneau dans la grille double (largeur colonne flexible, pas min-w fixe). */
  fillDualSlot?: boolean;
}

function MonthPanel({
  month,
  onMonthChange,
  range,
  onDayClick,
  boundEditors,
  today,
  monthNames,
  dayNames,
  todayLabel,
  clearBoundLabel,
  prevMonthLabel,
  nextMonthLabel,
  calendarGridLabel,
  disabled,
  expanded = false,
  fillDualSlot = false,
}: MonthPanelProps) {
  const safeMonth = startOfMonth(month);
  const gridStart = startOfWeek(startOfMonth(safeMonth));
  const days = eachDayOfInterval({
    start: gridStart,
    end: addDays(gridStart, CALENDAR_GRID_DAY_COUNT - 1),
  });

  const useUnifiedDualMetrics = fillDualSlot;
  const daySizeClass = useUnifiedDualMetrics ? "mx-auto h-9 w-full max-w-11" : expanded ? "mx-auto h-11 w-full max-w-11" : "h-9 w-9";
  const panelRootClass = fillDualSlot
    ? `${DUAL_CALENDAR_PANEL_CLASS} rounded-lg border border-gray-200 bg-white p-3 shadow-xs`
    : expanded
      ? "flex min-w-0 w-full flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-xs"
      : "flex min-w-[18rem] flex-col rounded-lg border border-gray-200 bg-white p-3 shadow-xs";
  const gridGapClass = "gap-1";
  const boundsLayoutClass =
    boundEditors.length > 1 ? "mt-3 grid grid-cols-1 gap-4 border-t border-gray-200 pt-3 md:grid-cols-2" : "mt-3 flex flex-col gap-2 border-t border-gray-200 pt-3";

  const singleBoundEditor = boundEditors.length === 1 ? boundEditors[0] : null;
  const singleBoundInputYmd = (() => {
    if (!singleBoundEditor) {
      return null;
    }
    const trimmed = singleBoundEditor.inputValue.trim();
    return trimmed && isValidYyyyMmDd(trimmed) ? trimmed : null;
  })();
  const singleBoundHasDate = singleBoundInputYmd != null;
  const daySelectedOutlineClass = "bg-blue-50 font-medium text-blue-800 border-2 border-blue-300";
  const dayTodayHoverLikeClass = "bg-gray-100 text-gray-900 hover:bg-gray-100";

  return (
    <div className={panelRootClass}>
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onMonthChange(subMonths(safeMonth, 1))}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50"
          aria-label={prevMonthLabel}
        >
          <FaChevronLeft className="h-3 w-3" />
        </button>
        <span className="text-sm font-semibold text-gray-800" aria-live="polite">
          {monthNames[safeMonth.getMonth()]} {format(safeMonth, "yyyy")}
        </span>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onMonthChange(addMonths(safeMonth, 1))}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50"
          aria-label={nextMonthLabel}
        >
          <FaChevronRight className="h-3 w-3" />
        </button>
      </div>
      <div className={`mb-2 grid grid-cols-7 ${gridGapClass}`}>
        {dayNames.map((day) => (
          <div key={day} className="flex h-7 items-center justify-center text-xs font-medium text-gray-500">
            {day.substring(0, 2)}
          </div>
        ))}
      </div>
      <div
        className={`grid flex-1 grid-cols-7 content-start ${gridGapClass} ${fillDualSlot ? "min-h-58" : ""}`}
        role="grid"
        aria-label={calendarGridLabel}
      >
        {days.map((day) => {
          const ymd = format(day, "yyyy-MM-dd");
          const inMonth = isSameMonth(day, safeMonth);
          const selected = isDayRangeEndpoint(ymd, range);
          const inRange = isDayInRange(ymd, range);
          const isToday = format(day, "yyyyMMdd") === format(today, "yyyyMMdd");
          const hasRangeSelection = Boolean(range.start || range.end);

          let dayClass = `flex items-center justify-center rounded-lg text-sm transition-colors focus:outline-hidden focus-visible:outline-hidden hover:bg-gray-100 focus-visible:bg-gray-100 ${daySizeClass} `;
          if (!inMonth) {
            dayClass += "text-gray-300 ";
          } else if (singleBoundEditor && singleBoundInputYmd === ymd) {
            dayClass += `${daySelectedOutlineClass} `;
          } else if (singleBoundEditor && isToday && !singleBoundHasDate) {
            dayClass += `${dayTodayHoverLikeClass} `;
          } else if (singleBoundEditor) {
            dayClass += "text-gray-700 hover:bg-gray-100 ";
          } else if (selected) {
            dayClass += "bg-blue-600 font-semibold text-white ";
          } else if (inRange) {
            dayClass += "bg-blue-100 text-blue-900 ";
          } else if (isToday && !hasRangeSelection) {
            dayClass += `${dayTodayHoverLikeClass} `;
          } else {
            dayClass += "text-gray-700 hover:bg-gray-100 ";
          }
          if (disabled) {
            dayClass += "cursor-not-allowed opacity-50 ";
          } else if (inMonth) {
            dayClass += "cursor-pointer ";
          }

          return (
            <button
              key={ymd}
              type="button"
              role="gridcell"
              disabled={disabled || !inMonth}
              className={dayClass}
              onClick={() => inMonth && onDayClick(ymd)}
              aria-label={ymd}
              aria-selected={inMonth && (selected || inRange) ? true : undefined}
              aria-current={inMonth && isToday ? "date" : undefined}
            >
              {format(day, "d")}
            </button>
          );
        })}
      </div>

      <div className={boundsLayoutClass}>
        {boundEditors.map((editor) => (
          <div key={editor.panelIndex} className="flex flex-col gap-2">
            <span className="text-xs font-medium text-gray-500">{editor.boundLabel}</span>
            <div className="flex items-stretch gap-1.5">
              <button
                type="button"
                disabled={disabled}
                onClick={editor.onTodayClick}
                className="shrink-0 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {todayLabel}
              </button>
              <input
                type="text"
                disabled={disabled}
                value={editor.inputValue}
                placeholder="yyyy-MM-dd"
                className="min-w-26 flex-1 rounded-lg border border-gray-300 bg-white px-2 py-2 text-xs text-gray-900 outline-hidden placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
                onFocus={editor.onInputFocus}
                onChange={(e) => editor.onInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    editor.onInputCommit();
                  }
                }}
                onBlur={() => {
                  editor.onInputCommit();
                }}
                aria-label={`${editor.boundLabel} (yyyy-MM-dd)`}
              />
              <button
                type="button"
                disabled={disabled}
                onClick={editor.onClearClick}
                className="shrink-0 rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={clearBoundLabel}
              >
                {clearBoundLabel}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const U2000SearchCriteriaCalendar: React.FC<U2000SearchCriteriaCalendarProps> = ({
  calendarCount = 2,
  range = {},
  onRangeChange,
  disabled = false,
  layout = "row",
}) => {
  const { t } = useTranslation("common");
  const today = useMemo(() => new Date(), []);
  const todayYmd = useMemo(() => format(today, "yyyy-MM-dd"), [today]);
  const [leftMonth, setLeftMonth] = useState(() => {
    if (range.start) {
      const parsed = parseISO(`${range.start}T00:00:00`);
      if (!Number.isNaN(parsed.getTime())) {
        return startOfMonth(parsed);
      }
    }
    return startOfMonth(subMonths(today, 1));
  });
  const [rightMonth, setRightMonth] = useState(() => {
    if (range.end) {
      const parsed = parseISO(`${range.end}T00:00:00`);
      if (!Number.isNaN(parsed.getTime())) {
        return startOfMonth(parsed);
      }
    }
    return startOfMonth(today);
  });
  const [editingPanel, setEditingPanel] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");

  const mergeSameMonth = calendarCount === 2 && areIntervalBoundsSameMonth(range.start, range.end);
  const prevMergeSameMonthRef = useRef(mergeSameMonth);

  const navigateToDate = useCallback((ymd: string, setMonth: (month: Date) => void) => {
    const parsed = parseISO(`${ymd}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      setMonth(startOfMonth(parsed));
    }
  }, []);

  const emitRangeChange = useCallback(
    (next: DateCriteriaRange) => {
      onRangeChange(normalizeIntervalRangeOrder(next));
    },
    [onRangeChange],
  );

  const selectionStatusMessage = useMemo(() => {
    if (calendarCount !== 2) {
      return "";
    }
    if (isIntervalRangeComplete(range)) {
      return t("lblSearchCriteriaIntervalSelectedRange", { range: formatIntervalDisplayValue(range) });
    }
    if (range.start && !range.end) {
      return t("lblSearchCriteriaIntervalPickEnd");
    }
    return t("lblSearchCriteriaIntervalPickStart");
  }, [calendarCount, range, t]);

  useEffect(() => {
    if (!mergeSameMonth || !range.start) {
      return;
    }
    const parsed = parseISO(`${range.start}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      setLeftMonth(startOfMonth(parsed));
    }
  }, [mergeSameMonth, range.start]);

  useEffect(() => {
    if (prevMergeSameMonthRef.current && !mergeSameMonth && range.end) {
      navigateToDate(range.end, setRightMonth);
    }
    prevMergeSameMonthRef.current = mergeSameMonth;
  }, [mergeSameMonth, range.end, navigateToDate]);

  const monthNames = useMemo(
    () => [
      t("lblCalendarJan"),
      t("lblCalendarFeb"),
      t("lblCalendarMar"),
      t("lblCalendarApr"),
      t("lblCalendarMay"),
      t("lblCalendarJun"),
      t("lblCalendarJul"),
      t("lblCalendarAug"),
      t("lblCalendarSep"),
      t("lblCalendarOct"),
      t("lblCalendarNov"),
      t("lblCalendarDec"),
    ],
    [t],
  );

  const dayNames = useMemo(
    () => [
      t("lblCalendarSun"),
      t("lblCalendarMon"),
      t("lblCalendarTue"),
      t("lblCalendarWed"),
      t("lblCalendarThu"),
      t("lblCalendarFri"),
      t("lblCalendarSat"),
    ],
    [t],
  );

  const startBoundLabel = t("lblSearchCriteriaIntervalStartBound");
  const endBoundLabel = t("lblSearchCriteriaIntervalEndBound");
  const prevMonthLabel = t("lblSearchCriteriaIntervalPrevMonth");
  const nextMonthLabel = t("lblSearchCriteriaIntervalNextMonth");
  const calendarGridLabel = t("lblSearchCriteriaIntervalCalendarGrid");
  const resetTodayLabel = t("lblSearchCriteriaIntervalResetToday");
  const clearBoundLabel = t("lblSearchCriteriaDateClear");

  const monthPanelA11y = {
    prevMonthLabel,
    nextMonthLabel,
    calendarGridLabel,
  };

  const setPanelBound = useCallback(
    (panelIndex: number, ymd: string, setMonth: (month: Date) => void) => {
      navigateToDate(ymd, setMonth);
      if (calendarCount === 1) {
        emitRangeChange(applyRangeDatePick(range, ymd));
        return;
      }
      if (panelIndex === 0) {
        emitRangeChange({ start: ymd, end: range.end });
      } else {
        emitRangeChange({ start: range.start, end: ymd });
      }
    },
    [calendarCount, emitRangeChange, navigateToDate, range],
  );

  const clearPanelBound = useCallback(
    (panelIndex: number) => {
      if (disabled) {
        return;
      }
      setEditingPanel(null);
      setEditingValue("");
      if (calendarCount === 1) {
        emitRangeChange({});
        return;
      }
      if (panelIndex === 0) {
        emitRangeChange({ start: undefined, end: range.end });
      } else {
        emitRangeChange({ start: range.start, end: undefined });
      }
    },
    [calendarCount, disabled, emitRangeChange, range.end, range.start],
  );

  const getBoundForPanel = useCallback(
    (panelIndex: number): string => {
      if (calendarCount === 1) {
        if (range.start && range.end) {
          return `${range.start} — ${range.end}`;
        }
        return range.start ?? range.end ?? "";
      }
      return panelIndex === 0 ? (range.start ?? "") : (range.end ?? "");
    },
    [calendarCount, range.end, range.start],
  );

  const getPanelInputValue = useCallback(
    (panelIndex: number): string => {
      if (editingPanel === panelIndex) {
        return editingValue;
      }
      return getBoundForPanel(panelIndex);
    },
    [editingPanel, editingValue, getBoundForPanel],
  );

  const makeBoundEditor = useCallback(
    (panelIndex: number, boundLabel: string, setMonth: (month: Date) => void): BoundEditorConfig => ({
      panelIndex,
      boundLabel,
      inputValue: getPanelInputValue(panelIndex),
      onInputFocus: () => {
        setEditingPanel(panelIndex);
        setEditingValue(getBoundForPanel(panelIndex));
      },
      onInputChange: (value) => {
        setEditingPanel(panelIndex);
        setEditingValue(value);
      },
      onInputCommit: () => {
        if (editingPanel !== null && editingPanel !== panelIndex) {
          return;
        }
        const trimmed = (editingPanel === panelIndex ? editingValue : getBoundForPanel(panelIndex)).trim();
        if (!trimmed) {
          setEditingPanel(null);
          setPanelBound(panelIndex, todayYmd, setMonth);
          return;
        }
        if (!isValidYyyyMmDd(trimmed)) {
          setEditingPanel(null);
          return;
        }
        setEditingPanel(null);
        setPanelBound(panelIndex, trimmed, setMonth);
      },
      onTodayClick: () => {
        if (disabled) {
          return;
        }
        setEditingPanel(null);
        setPanelBound(panelIndex, todayYmd, setMonth);
      },
      onClearClick: () => {
        clearPanelBound(panelIndex);
      },
    }),
    [clearPanelBound, disabled, editingPanel, editingValue, getBoundForPanel, getPanelInputValue, setPanelBound, todayYmd],
  );

  const handleDayClickOnMergedCalendar = useCallback(
    (ymd: string) => {
      if (disabled) {
        return;
      }
      setEditingPanel(null);
      navigateToDate(ymd, setLeftMonth);
      emitRangeChange(applyRangeDatePick(range, ymd));
    },
    [disabled, emitRangeChange, navigateToDate, range],
  );

  const handleDayClickOnPanel = useCallback(
    (panelIndex: number, ymd: string, setMonth: (month: Date) => void) => {
      if (disabled) {
        return;
      }
      setEditingPanel(null);
      setPanelBound(panelIndex, ymd, setMonth);
    },
    [disabled, setPanelBound],
  );

  const handleClear = useCallback(() => {
    emitRangeChange({ start: todayYmd, end: todayYmd });
    setEditingPanel(null);
    setEditingValue("");
  }, [emitRangeChange, todayYmd]);

  const handlePresetsRangeChange = useCallback(
    (next: DateCriteriaRange) => {
      setEditingPanel(null);
      emitRangeChange(next);
      if (next.start) {
        navigateToDate(next.start, setLeftMonth);
      }
      if (next.end) {
        navigateToDate(next.end, setRightMonth);
      }
    },
    [emitRangeChange, navigateToDate],
  );

  const useDualCalendarSlot = calendarCount === 2 && layout === "row";
  const panelsLayoutClass = layout === "row" ? "flex w-full flex-col gap-3 md:flex-row md:flex-wrap" : "flex flex-col gap-3";
  const mainRowClass = useDualCalendarSlot
    ? "flex w-full flex-col gap-3 xl:flex-row xl:items-stretch"
    : "flex w-full flex-col gap-3";

  const dualSlotPanelProps = { fillDualSlot: useDualCalendarSlot };

  const renderDualMonthPanels = () => (
    <>
      <MonthPanel
        {...dualSlotPanelProps}
        month={leftMonth}
        onMonthChange={setLeftMonth}
        range={range}
        onDayClick={(ymd) => handleDayClickOnPanel(0, ymd, setLeftMonth)}
        boundEditors={[makeBoundEditor(0, startBoundLabel, setLeftMonth)]}
        today={today}
        monthNames={monthNames}
        dayNames={dayNames}
        todayLabel={t("lblCalendarToday")}
        clearBoundLabel={clearBoundLabel}
        disabled={disabled}
        {...monthPanelA11y}
      />
      <MonthPanel
        {...dualSlotPanelProps}
        month={rightMonth}
        onMonthChange={setRightMonth}
        range={range}
        onDayClick={(ymd) => handleDayClickOnPanel(1, ymd, setRightMonth)}
        boundEditors={[makeBoundEditor(1, endBoundLabel, setRightMonth)]}
        today={today}
        monthNames={monthNames}
        dayNames={dayNames}
        todayLabel={t("lblCalendarToday")}
        clearBoundLabel={clearBoundLabel}
        disabled={disabled}
        {...monthPanelA11y}
      />
    </>
  );

  const renderMergedMonthPanel = () => (
    <MonthPanel
      expanded
      fillDualSlot={useDualCalendarSlot}
      month={leftMonth}
      onMonthChange={setLeftMonth}
      range={range}
      onDayClick={handleDayClickOnMergedCalendar}
      boundEditors={[
        makeBoundEditor(0, startBoundLabel, setLeftMonth),
        makeBoundEditor(1, endBoundLabel, setLeftMonth),
      ]}
      today={today}
      monthNames={monthNames}
      dayNames={dayNames}
      todayLabel={t("lblCalendarToday")}
      clearBoundLabel={clearBoundLabel}
      disabled={disabled}
      {...monthPanelA11y}
    />
  );

  const renderCalendarsInDualSlot = () => (
    <div className={DUAL_CALENDAR_SLOT_CLASS}>
      {mergeSameMonth ? <div className="flex min-h-110 min-w-0 md:col-span-2 md:h-full">{renderMergedMonthPanel()}</div> : renderDualMonthPanels()}
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-3">
      {calendarCount === 2 && selectionStatusMessage && (
        <div className="flex flex-col gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <p className="min-w-0 text-sm text-blue-900" role="status" aria-live="polite">
            {selectionStatusMessage}
          </p>
          <button
            type="button"
            disabled={disabled}
            onClick={handleClear}
            className="shrink-0 rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-sm font-medium text-blue-800 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {resetTodayLabel}
          </button>
        </div>
      )}
      <div className={mainRowClass}>
        <div className={`min-w-0 flex-1 ${useDualCalendarSlot ? "flex min-h-0 flex-col" : panelsLayoutClass}`}>
          {calendarCount === 1 && (
            <MonthPanel
              month={leftMonth}
              onMonthChange={setLeftMonth}
              range={range}
              onDayClick={(ymd) => handleDayClickOnPanel(0, ymd, setLeftMonth)}
              boundEditors={[makeBoundEditor(0, startBoundLabel, setLeftMonth)]}
              today={today}
              monthNames={monthNames}
              dayNames={dayNames}
              todayLabel={t("lblCalendarToday")}
              clearBoundLabel={clearBoundLabel}
              disabled={disabled}
              {...monthPanelA11y}
            />
          )}
          {calendarCount === 2 && (useDualCalendarSlot ? renderCalendarsInDualSlot() : mergeSameMonth ? renderMergedMonthPanel() : renderDualMonthPanels())}
        </div>
        {calendarCount === 2 && (
          <U2000SearchCriteriaIntervalPresetsPanel range={range} onRangeChange={handlePresetsRangeChange} disabled={disabled} />
        )}
      </div>
    </div>
  );
};

export default U2000SearchCriteriaCalendar;
